import {
  Slide,
  TextProps,
  BorderProps,
  TableCell,
  TableOptions,
  TableRow,
} from '@features/powerpoint';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { contentSlideHeader, textStyles } from '../../../../utils';
import { TopBottomPerformerKey } from '@components/Dashboard/dashboardSlice/optimizations';
import { formatters } from '@utils/formattersV2';
import { DIMENSIONS_PER_SLIDE } from '@features/lci-report/slides/slide-layout';
import { TopBottomPerformer } from '@features/reports/subdomains/visitation';

const fieldValueOptions: TextProps['options'] = {
  ...textStyles.contentSlideDefaults,
  h: 0.2,
  w: 3.26,
  fontSize: 13,
};

const NO_BORDER: BorderProps = { type: 'none' };

const HORIZONTAL_OFFSET = 4;

type LciOptimizationSlideProps = {
  slideMaster: string;
  topBottomPerformers: TopBottomPerformer | null;
};

export const lciOptimizationIndexSlide = (
  props: LciOptimizationSlideProps
): Slide => {
  const categoryTitles: TopBottomPerformerKey[] = props.topBottomPerformers
    ? (Object.keys(props.topBottomPerformers) as TopBottomPerformerKey[])
    : [];

  const formatPerformers = (
    category: TopBottomPerformerKey,
    level: 'top' | 'bottom'
  ): TableCell[][] => {
    const performers = props.topBottomPerformers?.[category]?.[level] ?? [];

    return performers.map((performer, i): TableCell[] => {
      const isLastElement = i === performers.length - 1;
      const bottomBorder: BorderProps = isLastElement
        ? NO_BORDER
        : {
            type: 'solid',
            color: unifiedPlatformColorPalette.inmarketGreen,
            pt: 1,
          };

      const textColor =
        (performer.visitMetrics.optimizationIndex ?? 0) < 0
          ? unifiedPlatformColorPalette.chartDarkRed
          : undefined;

      const optimizationIndexValue = formatters.number(
        performer.visitMetrics.optimizationIndex,
        {
          decimals: 2,
        }
      );

      return [
        {
          text: `${category.toUpperCase()} | ${performer.value}`,
          options: {
            align: 'left',
            valign: 'middle',
            border: [NO_BORDER, NO_BORDER, bottomBorder, NO_BORDER],
          },
        },
        {
          text: optimizationIndexValue,
          options: {
            valign: 'middle',
            align: 'right',
            border: [NO_BORDER, NO_BORDER, bottomBorder, NO_BORDER],
            color: textColor,
          },
        },
      ];
    });
  };

  const topPerformers = categoryTitles.slice(0, DIMENSIONS_PER_SLIDE).reduce(
    (
      accumulator: {
        tableRows: TableRow[];
        options: TableOptions;
      }[],
      categoryTitle,
      index
    ) => {
      const topRowCount =
        props.topBottomPerformers?.[categoryTitle]?.top.length ?? 0;
      const bottomRowCount =
        props.topBottomPerformers?.[categoryTitle]?.bottom.length ?? 0;

      const pushToTopPerformers = (
        level: 'top' | 'bottom',
        x: number,
        y: number
      ) => {
        const newRow = {
          tableRows: [...formatPerformers(categoryTitle, level)],
          options: {
            ...fieldValueOptions,
            w: 3.67,
            h: 1.22,
            x,
            y,
            rowH: 0.4,
            fontSize: 10,
          },
        };
        return newRow;
      };

      const topPerformerRows =
        topRowCount > 0
          ? [pushToTopPerformers('top', index * HORIZONTAL_OFFSET + 0.82, 3.14)]
          : [];
      const bottomPerformerRows =
        bottomRowCount > 0
          ? [
              pushToTopPerformers(
                'bottom',
                index * HORIZONTAL_OFFSET + 0.82,
                5.22
              ),
            ]
          : [];

      return [...accumulator, ...topPerformerRows, ...bottomPerformerRows];
    },
    []
  );

  const createTopBottomTextElements = (
    performers: TopBottomPerformer | null,
    categoryIndex: number,
    level: 'top' | 'bottom'
  ): TextProps[] => {
    const title = categoryTitles[categoryIndex];

    if (!title) {
      return [];
    }

    const positionY = level === 'top' ? 2.4 : 4.49;

    return (performers?.[title]?.[level].length ?? 0) > 0
      ? [
          {
            text: `${title} ${
              level === 'top' ? 'Top' : 'Bottom'
            }`.toUpperCase(),
            options: {
              color: unifiedPlatformColorPalette.inmarketGreen,
              fontSize: 14,
              x: categoryIndex * HORIZONTAL_OFFSET + 0.82,
              y: positionY,
              w: 3.67,
              h: 1.22,
            },
          },
        ]
      : [];
  };

  const topTextElements = topPerformers
    .slice(0, DIMENSIONS_PER_SLIDE)
    .flatMap((_performer, index) =>
      createTopBottomTextElements(props.topBottomPerformers, index, 'bottom')
    );

  const bottomTextElements = topPerformers
    .slice(0, DIMENSIONS_PER_SLIDE)
    .flatMap((_performer, index) =>
      createTopBottomTextElements(props.topBottomPerformers, index, 'top')
    );

  return {
    slideMaster: props.slideMaster,
    tables: topPerformers,
    texts: [
      contentSlideHeader('LCI Optimization Index'),
      {
        text: 'Shown are the top 3 and bottom 3 performers in each dimension by optimization index. An index above 1 means this sub-dimension had a higher Incremental Lift result than the overall campaign. All results shown have a confidence level of 90% or above.',
        options: {
          ...fieldValueOptions,
          x: 0.74,
          y: 1.77,
          h: 0.67,
          w: 11.6,
        },
      },
      ...topTextElements,
      ...bottomTextElements,
    ],
  };
};
